/**
 * Чистая логика карточки SEO: форма редактора, изменения, лимиты и счётчики,
 * нормализация замечаний. Без React — проверяется без рендера и используется
 * обеими вкладками модалки.
 */

import type {
  SeoCardGenerationPreview,
  SeoCardIssue,
  SeoCardOverview,
  SeoContentFields,
  SeoTextValidationIssue,
} from '../seo/api'
import { issueBlocksMarketplaceReadiness, issueBlocksTextApproval } from '../seo/workbench'

/** Поля, которые редактирует оператор. */
export type SeoCardField = 'title' | 'annotation' | 'description'
export const CARD_FIELDS: readonly SeoCardField[] = ['title', 'annotation', 'description']

/** Форма редактора: три поля плюс характеристики. */
export interface CardForm {
  title: string
  annotation: string
  description: string
  characteristics: Record<string, string>
}

/** Значение характеристики в строку: массивы через запятую, числа как есть. */
function characteristicToText(value: string | number | string[] | null | undefined): string {
  if (Array.isArray(value)) return value.join(', ')
  if (value == null) return ''
  return String(value)
}

function characteristicsFromFields(
  values: SeoContentFields['characteristics'],
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [name, value] of Object.entries(values || {})) {
    result[name] = characteristicToText(value)
  }
  return result
}

/** Форма из полей ответа сервера. */
export function formFromFields(fields: SeoContentFields): CardForm {
  return {
    title: typeof fields.title === 'string' ? fields.title : '',
    annotation: typeof fields.annotation === 'string' ? fields.annotation : '',
    description: typeof fields.description === 'string' ? fields.description : '',
    characteristics: characteristicsFromFields(fields.characteristics),
  }
}

/** Форма из ответа сервера. */
export function formFromCard(card: SeoCardOverview): CardForm {
  return formFromFields(card.fields || {})
}

/** Что изменил оператор: ключи полей и характеристик. */
export function dirtyKeys(form: CardForm, initial: CardForm): string[] {
  const keys: string[] = []
  for (const field of CARD_FIELDS) {
    if (form[field] !== initial[field]) keys.push(field)
  }
  const names = new Set([
    ...Object.keys(form.characteristics),
    ...Object.keys(initial.characteristics),
  ])
  for (const name of names) {
    if ((form.characteristics[name] ?? '') !== (initial.characteristics[name] ?? '')) {
      keys.push(`characteristics.${name}`)
    }
  }
  return keys
}

/** Payload для saveCardDraft: только поля, не пустые ключи характеристик. */
export function fieldsFromForm(form: CardForm): SeoContentFields {
  const characteristics: Record<string, string> = {}
  for (const [name, value] of Object.entries(form.characteristics)) {
    if (value.trim()) characteristics[name] = value
  }
  return {
    title: form.title,
    annotation: form.annotation,
    description: form.description,
    characteristics,
  }
}

/** Значение поля формы по пути: 'title' либо 'characteristics.<имя>'. */
export function readFormField(form: CardForm, path: string): string {
  if (path.startsWith('characteristics.')) {
    return form.characteristics[path.slice('characteristics.'.length)] ?? ''
  }
  return form[path as SeoCardField] ?? ''
}

/** Новое значение поля формы по пути; характеристики создаются на лету. */
export function writeFormField(form: CardForm, path: string, value: string): CardForm {
  if (path.startsWith('characteristics.')) {
    const name = path.slice('characteristics.'.length)
    return { ...form, characteristics: { ...form.characteristics, [name]: value } }
  }
  return { ...form, [path]: value }
}

/** Значение поля ответа сервера по пути: 'title' либо 'characteristics.<имя>'. */
export function readContentField(fields: SeoContentFields, path: string): string {
  if (path.startsWith('characteristics.')) {
    return characteristicToText(fields.characteristics?.[path.slice('characteristics.'.length)])
  }
  return characteristicToText(fields[path as SeoCardField])
}

/**
 * Серверные поля поверх формы: перезаписываются только переданные ключи,
 * остальные значения формы сохраняются.
 */
export function mergeFormWithFields(form: CardForm, fields: SeoContentFields): CardForm {
  const next: CardForm = { ...form, characteristics: { ...form.characteristics } }
  for (const field of CARD_FIELDS) {
    if (typeof fields[field] === 'string') next[field] = fields[field] as string
  }
  for (const [name, value] of Object.entries(fields.characteristics || {})) {
    next.characteristics[name] = characteristicToText(value)
  }
  return next
}

/**
 * Лимит длины поля, если он известен. Источник — ответ предпросмотра
 * (`text_validation.policies[field].policy.max_length`) либо ответ проверки.
 * Неизвестен — null, и тогда показывается только число знаков.
 */
export function fieldLimit(
  field: SeoCardField,
  policies: Record<string, { policy?: { max_length?: number | null } }> | null | undefined,
): number | null {
  const limit = policies?.[field]?.policy?.max_length
  return typeof limit === 'number' ? limit : null
}

/** Тон счётчика: до 80 % лимита — серый, до 100 % — янтарный, свыше — красный. */
export function counterTone(length: number, limit: number | null): 'gray' | 'amber' | 'red' {
  if (limit == null || limit <= 0) return 'gray'
  if (length > limit) return 'red'
  if (length > limit * 0.8) return 'amber'
  return 'gray'
}

/** Доказательство замечания для ключа дедупликации. */
function issueEvidence(issue: Pick<SeoCardIssue, 'details'>): string {
  const details = issue.details
  return String(
    details?.fragment
      ?? details?.keyword
      ?? details?.claim
      ?? details?.variable
      ?? details?.word
      ?? details?.invalid
      ?? '',
  )
}

/** Ключ дедупликации замечания: code:field:evidence. */
export function issueKey(issue: Pick<SeoCardIssue, 'code' | 'field' | 'details'>): string {
  return `${issue.code}:${issue.field || ''}:${JSON.stringify(issueEvidence(issue))}`
}

/** Дедупликация замечаний по ключу code:field:evidence. */
export function dedupeIssues(issues: SeoCardIssue[]): SeoCardIssue[] {
  const seen = new Set<string>()
  return issues.filter((issue) => {
    const key = issueKey(issue)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Замечание текстовой проверки предпросмотра в формате замечаний карточки. */
export function toCardIssue(issue: SeoTextValidationIssue): SeoCardIssue {
  return {
    code: issue.code,
    severity: issue.severity === 'blocker' ? 'critical' : issue.severity === 'info' ? 'tip' : 'warning',
    field: issue.field,
    message: issue.message,
    reason: issue.rule_source,
    action: issue.remediation_hint,
    group: issue.group,
    scope: issue.scope,
    blocks: issue.blocks,
    remediation: issue.remediation,
    details: issue.details,
  }
}

/** Замечания текстовой проверки из предпросмотра генерации. */
export function previewIssues(preview: SeoCardGenerationPreview | null): SeoTextValidationIssue[] {
  return preview?.text_validation?.issues ?? []
}

/** Можно ли принять результат предпросмотра; null — текстовой проверки ещё не было. */
export function previewIsApprovable(preview: SeoCardGenerationPreview | null): boolean | null {
  return preview?.text_validation ? preview.text_validation.approvable : null
}

/** Замечания, которые вернул ответ с ошибкой утверждения. */
export function requestIssues(error: unknown): SeoCardIssue[] {
  const candidate = error as { response?: { data?: { error?: { details?: { issues?: SeoCardIssue[] } } } } }
  const issues = candidate.response?.data?.error?.details?.issues
  return Array.isArray(issues) ? issues : []
}

/** Валидация из замечаний ответа утверждения: сервер вернул список вместо успеха. */
export function validationFromApprovalError(
  issues: SeoCardIssue[],
  approvalMode: SeoCardOverview['validation']['approval_mode'],
): SeoCardOverview['validation'] {
  const textBlockers = issues.filter(issueBlocksTextApproval).length
  return {
    issues,
    critical: issues.filter((issue) => issue.severity === 'critical').length,
    warnings: issues.filter((issue) => issue.severity === 'warning').length,
    tips: issues.filter((issue) => issue.severity === 'tip').length,
    publishable: false,
    text_approvable: textBlockers === 0,
    approval_blockers: textBlockers,
    readiness_blockers: issues.filter(issueBlocksMarketplaceReadiness).length,
    approval_mode: approvalMode,
  }
}

/** Операторское сообщение о результате предпросмотра генерации; null — показывать нечего. */
export function previewNotice(
  preview: SeoCardGenerationPreview,
): { message: string; tone: 'warning' | 'info'; openChecks: boolean } | null {
  if (preview.text_validation && !preview.text_validation.approvable) {
    return {
      message: `Предпросмотр содержит блокирующие ошибки: ${preview.text_validation.blockers}`,
      tone: 'warning',
      openChecks: true,
    }
  }
  if (preview.missing_variables.length) {
    return {
      message: 'Не заполнены данные, необходимые профилю генерации. Откройте проверки для подробностей.',
      tone: 'warning',
      openChecks: false,
    }
  }
  if (!preview.diff.length) return { message: 'Шаблоны не предлагают изменений', tone: 'info', openChecks: false }
  return null
}
